import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Check, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { CartItem } from "@/lib/cart";
import { useIsMobile } from "@/hooks/use-mobile";

interface AddToCartPopupProps {
  item: CartItem | null;
  onClose: () => void;
}

export function AddToCartPopup({ item, onClose }: AddToCartPopupProps) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (item) {
      // Trigger animation
      requestAnimationFrame(() => setIsVisible(true));
      
      // Auto-close after 4 seconds
      const timer = setTimeout(() => {
        setIsVisible(false);
        setTimeout(onClose, 200); // Wait for exit animation
      }, 4000);
      
      return () => clearTimeout(timer);
    }
  }, [item, onClose]);

  const handleViewCart = () => {
    setIsVisible(false);
    setTimeout(() => {
      onClose();
      navigate("/cart");
    }, 200);
  };

  const handleContinueShopping = () => {
    setIsVisible(false);
    setTimeout(onClose, 200);
  };

  if (!item) return null;

  const formatCurrency = (amount: number) => {
    return t('common.currencyFormat', { amount: amount.toLocaleString("sv-SE") });
  };

  // Mobile: centered modal overlay
  if (isMobile) {
    return (
      <div 
        className={cn(
          "fixed inset-0 z-50 flex items-center justify-center p-4 transition-opacity duration-200",
          isVisible ? "opacity-100" : "opacity-0"
        )}
      >
        {/* Backdrop */}
        <div 
          className="absolute inset-0 bg-background/80 backdrop-blur-sm"
          onClick={handleContinueShopping}
        />
        
        {/* Popup card */}
        <div 
          className={cn(
            "relative bg-card border border-border rounded-xl shadow-xl p-5 w-full max-w-sm transition-all duration-200",
            isVisible ? "scale-100 opacity-100" : "scale-95 opacity-0"
          )}
        >
          {/* Close button */}
          <button
            onClick={handleContinueShopping}
            className="absolute top-3 right-3 p-1 rounded-full hover:bg-muted transition-colors"
            aria-label={t('actions.cancel')}
          >
            <X className="h-4 w-4 text-muted-foreground" />
          </button>

          {/* Success header */}
          <div className="flex items-center gap-2 mb-4">
            <div className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center">
              <Check className="h-4 w-4 text-primary" />
            </div>
            <span className="font-medium text-sm">{t('cartPopup.addedToCart')}</span>
          </div>

          {/* Product info */}
          <div className="flex gap-4 mb-5">
            <img
              src={item.imageUrl}
              alt={item.title}
              className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
            />
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-sm line-clamp-2 mb-1">{item.title}</h4>
              {item.artistName && (
                <p className="text-xs text-muted-foreground mb-1">{item.artistName}</p>
              )}
              <p className="text-sm text-muted-foreground">
                {item.quantity} {t('cartPopup.pieces')} • {formatCurrency(item.price * item.quantity)}
              </p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col gap-2">
            <Button onClick={handleViewCart} className="w-full">
              {t('cartPopup.viewCart')}
            </Button>
            <Button variant="outline" onClick={handleContinueShopping} className="w-full">
              {t('cartPopup.continueShopping')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Desktop: positioned popup in top right
  return (
    <div 
      className={cn(
        "fixed top-20 right-4 z-50 bg-card border border-border rounded-xl shadow-xl p-4 w-80 transition-all duration-200",
        isVisible 
          ? "translate-x-0 opacity-100" 
          : "translate-x-4 opacity-0"
      )}
    >
      {/* Close button */}
      <button
        onClick={handleContinueShopping}
        className="absolute top-3 right-3 p-1 rounded-full hover:bg-muted transition-colors"
        aria-label={t('actions.cancel')}
      >
        <X className="h-4 w-4 text-muted-foreground" />
      </button>

      {/* Success header */}
      <div className="flex items-center gap-2 mb-3">
        <div className="flex-shrink-0 w-5 h-5 rounded-full bg-primary/10 flex items-center justify-center">
          <Check className="h-3 w-3 text-primary" />
        </div>
        <span className="font-medium text-sm">{t('cartPopup.addedToCart')}</span>
      </div>

      {/* Product info */}
      <div className="flex gap-3 mb-4">
        <img
          src={item.imageUrl}
          alt={item.title}
          className="w-16 h-16 object-cover rounded-lg flex-shrink-0"
        />
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-sm line-clamp-2 mb-0.5">{item.title}</h4>
          {item.artistName && (
            <p className="text-xs text-muted-foreground">{item.artistName}</p>
          )}
          <p className="text-sm font-medium text-primary mt-1">
            {item.quantity} {t('cartPopup.pieces')} • {formatCurrency(item.price * item.quantity)}
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={handleContinueShopping} className="flex-1">
          {t('cartPopup.continueShopping')}
        </Button>
        <Button size="sm" onClick={handleViewCart} className="flex-1">
          {t('cartPopup.viewCart')}
        </Button>
      </div>
    </div>
  );
}
